import { useState } from "react";
import type { Source } from "../app";

interface FootnotesProps {
  sources: Source[];
  onSourceClick?: (url: string) => void;
}

export function Footnotes({ sources, onSourceClick }: FootnotesProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedItem, setExpandedItem] = useState<number | null>(null);

  const toggleExpand = (index: number) => {
    setExpandedItem(expandedItem === index ? null : index);
  };

  return (
    <aside className={`footnotes sources-footnotes ${isExpanded ? "is-expanded" : ""}`}>
      <div
        className="footnotes-header"
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <h3 className="footnotes-title">
          <span className="footnotes-icon">&#128209;</span>
          Sources ({sources.length})
        </h3>
        <button
          className="footnotes-toggle"
          aria-label={isExpanded ? "Masquer les sources" : "Afficher les sources"}
          aria-expanded={isExpanded}
        >
          {isExpanded ? "Masquer" : "Afficher"}
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </div>

      <div className="footnotes-body">
        <div className="footnotes-body-inner">
          <ol className="footnotes-list">
            {sources.map((source, index) => (
              <li
                key={index}
                id={`fn-${index + 1}`}
                className={`footnote-item ${expandedItem === index ? "is-expanded" : ""}`}
              >
                <div
                  className="footnote-header"
                  onClick={() => toggleExpand(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleExpand(index);
                    }
                  }}
                >
                  <span className="footnote-num">{index + 1}</span>
                  <span className="footnote-citation">
                    {source.kind === "decision" ? (
                      <>
                        <strong>{source.sectionPath}</strong>
                        <span className="footnote-idcc">{source.articleNum}</span>
                      </>
                    ) : (
                      <>
                        <strong>Art. {source.articleNum}</strong>
                        {source.idcc && <span className="footnote-idcc">IDCC {source.idcc}</span>}
                        {source.sectionPath && (
                          <span className="footnote-section" title={source.sectionPath}>
                            {source.sectionPath}
                          </span>
                        )}
                      </>
                    )}
                  </span>
                  <span
                    className="footnote-score"
                    title={`Pertinence: ${Math.round(source.score * 100)}%`}
                  >
                    {Math.round(source.score * 100)}%
                  </span>
                  <button
                    className="footnote-toggle"
                    aria-label={expandedItem === index ? "Reduire" : "Developper"}
                    aria-expanded={expandedItem === index}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                </div>

                {expandedItem === index && (
                  <div className="footnote-details">
                    {source.kind === "decision" ? (
                      <>
                        <p className="footnote-convention">
                          Judilibre {source.decisionId}
                          {source.chamber ? ` · ${source.chamber}` : ""}
                          {source.decisionDate ? ` · ${source.decisionDate}` : ""}
                        </p>
                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                          {onSourceClick && (
                            <button
                              className="footnote-link"
                              onClick={() => onSourceClick(source.articleUrl)}
                              style={{ cursor: "pointer" }}
                              title="Copier l'identifiant Judilibre"
                            >
                              Copier l&apos;id
                            </button>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        {"conventionTitle" in source && source.conventionTitle && (
                          <p className="footnote-convention">{source.conventionTitle}</p>
                        )}
                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                          {onSourceClick && (
                            <button
                              className="footnote-link"
                              onClick={() => onSourceClick(source.articleUrl)}
                              style={{ cursor: "pointer" }}
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ marginRight: "4px" }}
                              >
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                <line x1="9" y1="3" x2="9" y2="21" />
                              </svg>
                              Apercu
                            </button>
                          )}
                          <a
                            href={source.articleUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="footnote-link"
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{ marginRight: "4px" }}
                            >
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                              <polyline points="15 3 21 3 21 9" />
                              <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                            Legifrance
                          </a>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </aside>
  );
}
